import { useState, useEffect, FormEvent, ChangeEvent } from 'react'
import { loadCaptchaEnginge, LoadCanvasTemplate, validateCaptcha } from 'react-simple-captcha'
import api from '../../../services/api'

interface FormData {
  name: string
  email: string
  subject: string
  message: string
  captcha: string
  consent_rgpd: boolean
}

interface FormErrors {
  name?: string
  email?: string
  subject?: string
  message?: string
  captcha?: string
  consent_rgpd?: string
}

function ContactForm() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
    captcha: '',
    consent_rgpd: false
  })

  useEffect(() => {
    loadCaptchaEnginge(6)
  }, [])

  const [errors, setErrors] = useState<FormErrors>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null
    message: string
  }>({ type: null, message: '' })

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis'
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Le nom doit contenir au moins 2 caractères'
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis'
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'L\'email n\'est pas valide'
    }

    // Subject validation
    if (!formData.subject.trim()) {
      newErrors.subject = 'Le sujet est requis'
    } else if (formData.subject.trim().length < 3) {
      newErrors.subject = 'Le sujet doit contenir au moins 3 caractères'
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Le message est requis'
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Le message doit contenir au moins 10 caractères'
    }

    // Captcha validation
    if (!formData.captcha.trim()) {
      newErrors.captcha = 'Veuillez saisir le code de sécurité'
    } else if (!validateCaptcha(formData.captcha, false)) {
      newErrors.captcha = 'Le code de sécurité est incorrect'
    }

    // RGPD consent validation
    if (!formData.consent_rgpd) {
      newErrors.consent_rgpd = 'Vous devez accepter la politique de confidentialité'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target
    const checked = (e.target as HTMLInputElement).checked

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))

    // Clear error for this field
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }))
    }

    // Clear submit status when user starts typing
    if (submitStatus.type) {
      setSubmitStatus({ type: null, message: '' })
    }
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setSubmitting(true)
    setSubmitStatus({ type: null, message: '' })

    try {
      await api.post('/contact', {
        name: formData.name.trim(),
        email: formData.email.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        consent_rgpd: formData.consent_rgpd
      })

      setSubmitStatus({
        type: 'success',
        message: 'Votre message a été envoyé avec succès. Nous vous répondrons dans les plus brefs délais.'
      })

      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        captcha: '',
        consent_rgpd: false
      })
      setErrors({})
      loadCaptchaEnginge(6)
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error instanceof Error ? error.message : 'Une erreur est survenue lors de l\'envoi du message. Veuillez réessayer.'
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="contact-form-container">
      <h2>Envoyez-nous un message</h2>

      <form onSubmit={handleSubmit} className="contact-form" noValidate>
        {/* Name field */}
        <div className="form-group">
          <label htmlFor="name">
            Nom complet <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={errors.name ? 'error' : ''}
            placeholder="Votre nom"
            disabled={submitting}
          />
          {errors.name && <span className="error-message">{errors.name}</span>}
        </div>

        {/* Email field */}
        <div className="form-group">
          <label htmlFor="email">
            Email <span className="required">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={errors.email ? 'error' : ''}
            placeholder="votre.email@exemple.com"
            disabled={submitting}
          />
          {errors.email && <span className="error-message">{errors.email}</span>}
        </div>

        {/* Subject field */}
        <div className="form-group">
          <label htmlFor="subject">
            Sujet <span className="required">*</span>
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            className={errors.subject ? 'error' : ''}
            placeholder="Objet de votre message"
            disabled={submitting}
          />
          {errors.subject && <span className="error-message">{errors.subject}</span>}
        </div>

        {/* Message field */}
        <div className="form-group">
          <label htmlFor="message">
            Message <span className="required">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            className={errors.message ? 'error' : ''}
            placeholder="Votre message..."
            rows={6}
            disabled={submitting}
          />
          {errors.message && <span className="error-message">{errors.message}</span>}
        </div>

        {/* Captcha field */}
        <div className="form-group">
          <label htmlFor="captcha">
            Code de sécurité <span className="required">*</span>
          </label>
          <div className="captcha-container">
            <LoadCanvasTemplate />
          </div>
          <input
            type="text"
            id="captcha"
            name="captcha"
            value={formData.captcha}
            onChange={handleChange}
            className={errors.captcha ? 'error' : ''}
            placeholder="Saisissez le code ci-dessus"
            disabled={submitting}
          />
          {errors.captcha && <span className="error-message">{errors.captcha}</span>}
        </div>

        {/* RGPD consent */}
        <div className="form-group checkbox-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="consent_rgpd"
              checked={formData.consent_rgpd}
              onChange={handleChange}
              className={errors.consent_rgpd ? 'error' : ''}
              disabled={submitting}
            />
            <span>
              J'accepte que mes données personnelles soient utilisées pour répondre à ma demande. <span className="required">*</span>
            </span>
          </label>
          {errors.consent_rgpd && <span className="error-message">{errors.consent_rgpd}</span>}
        </div>

        {/* Submit status messages */}
        {submitStatus.type && (
          <div className={`submit-status ${submitStatus.type}`}>
            {submitStatus.message}
          </div>
        )}

        {/* Submit button */}
        <button
          type="submit"
          className="submit-button"
          disabled={submitting}
        >
          {submitting ? 'Envoi en cours...' : 'Envoyer le message'}
        </button>
      </form>
    </div>
  )
}

export default ContactForm
